import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Button,
  IconButton,
  Divider,
  TextField,
  Card,
  CardMedia,
  CardContent,
  Alert,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCart } from "../contexts/CartContext";
import { resetCheckout } from "../store/slices/checkoutSlice";
import { formatCurrency } from "../utils/formatters";
import EmptyState from "../components/common/EmptyState";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getDefaultTaxRate } from "../store/slices/taxSlice";

/**
 * Cart page component
 * @returns {JSX.Element} CartPage component
 */
const CartPage = () => {
  const { t } = useTranslation("common");
  const { t: tCart } = useTranslation("cart");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cart, loading, error, updateCartItem, removeFromCart, clearCart } =
    useCart();
  const { taxRate } = useSelector((state) => state.tax);

  // Add state for item-specific errors
  const [itemErrors, setItemErrors] = React.useState({});
  // Track stock limitations for each item
  const [stockLimits, setStockLimits] = React.useState({});

  // Fetch tax rate when component mounts
  useEffect(() => {
    dispatch(getDefaultTaxRate());
  }, [dispatch]);

  // Handle quantity change
  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    // If trying to add more than available stock, show error but don't attempt API call
    if (
      stockLimits[itemId] !== undefined &&
      newQuantity > stockLimits[itemId]
    ) {
      toast.error(
        tCart("shared.stockLimitation", { count: stockLimits[itemId] })
      );
      setItemErrors((prev) => ({
        ...prev,
        [itemId]: tCart("shared.stockLimitation", {
          count: stockLimits[itemId],
        }),
      }));
      return;
    }

    try {
      // Clear any existing error for this item
      setItemErrors((prev) => ({ ...prev, [itemId]: null }));
      await updateCartItem(itemId, newQuantity);
    } catch (error) {
      console.error("Error updating cart item:", error);

      // Handle stock validation error
      if (
        error.message === "Not enough stock available" &&
        error.availableStock !== undefined
      ) {
        // Remember the stock limit for this item to prevent future attempts
        setStockLimits((prev) => ({
          ...prev,
          [itemId]: error.availableStock,
        }));

        // Set item-specific error
        setItemErrors((prev) => ({
          ...prev,
          [itemId]: tCart("shared.stockLimitation", {
            count: error.availableStock,
          }),
        }));

        // Show toast but don't disrupt the UI
        toast.error(
          tCart("shared.stockLimitation", { count: error.availableStock })
        );
      } else {
        // Handle any other error message
        const errorMessage =
          error.message || tCart("context.error.updateFailed");
        setItemErrors((prev) => ({
          ...prev,
          [itemId]: errorMessage,
        }));
        toast.error(errorMessage);
      }
    }
  };

  // Initialize stock limits on component mount or when cart changes
  React.useEffect(() => {
    if (cart?.items) {
      const limits = {};
      cart.items.forEach((item) => {
        // If product has stock information, use it as initial limit
        if (item.product && item.product.stock !== undefined) {
          limits[item.id] = item.product.stock;
        }
      });
      setStockLimits(limits);
    }
  }, [cart]);

  // Handle remove item
  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
  };

  // Handle clear cart
  const handleClearCart = () => {
    clearCart();
  };

  // Proceed to checkout
  const handleGoToCheckout = () => {
    // Reset checkout state BEFORE navigating to ensure a fresh start
    dispatch(resetCheckout());
    console.log("CartPage: Dispatched resetCheckout");
    navigate("/checkout", {
      state: { fromCart: true },
    });
  };

  // Handle continue shopping
  const handleContinueShopping = () => {
    navigate("/products");
  };

  // Render loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleContinueShopping}>
          {tCart("page.actions.continueShopping")}
        </Button>
      </Container>
    );
  }

  // Render empty cart
  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <EmptyState
          icon={<ShoppingCartIcon sx={{ fontSize: 60 }} />}
          title={tCart("page.emptyState.title")}
          description={tCart("page.emptyState.description")}
          actionText={tCart("page.emptyState.actionText")}
          onAction={handleContinueShopping}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {tCart("page.yourCart")}
      </Typography>

      <Grid container spacing={3}>
        {/* Cart Items */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, mb: 2 }}>
            {cart.items
              .slice()
              .sort((a, b) => {
                // Sort by product name first
                const nameA = a.product?.name?.toLowerCase() || "";
                const nameB = b.product?.name?.toLowerCase() || "";

                // First by name
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;

                // If names are the same, sort by variation name if available
                if (a.product?.variation && b.product?.variation) {
                  const varNameA = a.product.variation.name.toLowerCase();
                  const varNameB = b.product.variation.name.toLowerCase();
                  return varNameA.localeCompare(varNameB);
                }

                return 0;
              })
              .map((item) => (
                <Box key={item.id} sx={{ mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Product Image */}
                    <Grid item xs={3} sm={2}>
                      <Link
                        to={`/products/${
                          item.product?.parentId || item.product?.id
                        }`}
                        style={{ textDecoration: "none" }}
                      >
                        <Card elevation={0}>
                          <CardMedia
                            component="img"
                            height="80"
                            image={
                              item.product?.imageUrl ||
                              item.product?.parentProduct?.imageUrl ||
                              item.product?.parent?.imageUrl ||
                              "/placeholder.svg"
                            }
                            alt={item.product?.name}
                            sx={{ objectFit: "contain" }}
                          />
                        </Card>
                      </Link>
                    </Grid>

                    {/* Product Details */}
                    <Grid item xs={9} sm={5}>
                      <Link
                        to={`/products/${
                          item.product?.parentId || item.product?.id
                        }`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item.product?.name}
                          {item.product?.variation && (
                            <>
                              {" - "}
                              {item.product.variation.name.startsWith(
                                item.product.name
                              )
                                ? item.product.variation.name
                                    .substring(item.product.name.length)
                                    .trim()
                                    .replace(/^-\s*/, "")
                                : item.product.variation.name}
                            </>
                          )}
                        </Typography>
                      </Link>
                      <Typography variant="body2" color="text.secondary">
                        {tCart("page.productDetails.sku", {
                          sku: item.product?.sku,
                        })}
                      </Typography>
                      {item.options && Object.keys(item.options).length > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {Object.entries(item.options)
                            .filter(
                              ([key]) =>
                                !["variationId", "useVariationStock"].includes(
                                  key
                                )
                            )
                            .map(([key, value]) => {
                              // Format the key from camelCase to Title Case
                              const formattedKey = key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase());
                              return `${formattedKey}: ${value}`;
                            })
                            .join(", ")}
                        </Typography>
                      )}
                    </Grid>

                    {/* Quantity Controls */}
                    <Grid item xs={6} sm={2}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Box display="flex" alignItems="center">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleQuantityChange(item.id, item.quantity - 1)
                            }
                            disabled={item.quantity <= 1}
                            aria-label={tCart("page.quantity.decrease")}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Box
                            sx={{
                              width: "60px",
                              mx: 1,
                              border: "1px solid rgba(0, 0, 0, 0.23)",
                              borderRadius: "4px",
                              display: "flex",
                              justifyContent: "center",
                              padding: "5px 0",
                            }}
                          >
                            <Typography>{item.quantity}</Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleQuantityChange(item.id, item.quantity + 1)
                            }
                            disabled={
                              stockLimits[item.id] !== undefined &&
                              item.quantity >= stockLimits[item.id]
                            }
                            title={
                              stockLimits[item.id] !== undefined &&
                              item.quantity >= stockLimits[item.id]
                                ? tCart("shared.maxStockReached")
                                : tCart("page.quantity.increase")
                            }
                            aria-label={tCart("page.quantity.increase")}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {itemErrors[item.id] && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, textAlign: "center" }}
                          >
                            {itemErrors[item.id]}
                          </Typography>
                        )}
                        {!itemErrors[item.id] &&
                          stockLimits[item.id] !== undefined &&
                          item.quantity >= stockLimits[item.id] && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, textAlign: "center" }}
                            >
                              {tCart("shared.maxStockReached")}
                            </Typography>
                          )}
                      </Box>
                    </Grid>

                    {/* Price */}
                    <Grid item xs={4} sm={2} textAlign="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        {formatCurrency(item.price)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(item.price * item.quantity)}
                      </Typography>
                    </Grid>

                    {/* Remove Button */}
                    <Grid item xs={2} sm={1} textAlign="right">
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(item.id)}
                        aria-label={tCart("page.productDetails.removeItem")}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                </Box>
              ))}

            {/* Cart Actions */}
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button variant="outlined" onClick={handleContinueShopping}>
                {tCart("page.actions.continueShopping")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearCart}
                startIcon={<DeleteIcon />}
              >
                {tCart("page.actions.clearCart")}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {tCart("orderSummary.title")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body1">
                {tCart("orderSummary.subtotal")}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(cart.subtotal)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body1">
                {tCart("orderSummary.taxWithRate", {
                  rate: cart.taxRate
                    ? (cart.taxRate * 100).toFixed(1)
                    : taxRate?.rate
                    ? (taxRate.rate * 100).toFixed(1)
                    : 7.7,
                })}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(cart.tax)}
              </Typography>
            </Box>

            {cart.discount > 0 && (
              <Box
                display="flex"
                justifyContent="space-between"
                mb={1}
                sx={{ color: "success.main" }}
              >
                <Typography variant="body1">
                  {cart.couponCode
                    ? tCart("orderSummary.discountWithCode", {
                        code: cart.couponCode,
                      })
                    : tCart("orderSummary.discount")}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  -{formatCurrency(cart.discount)}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                {tCart("orderSummary.total")}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(cart.total)}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleGoToCheckout}
              disabled={!cart.items || cart.items.length === 0}
            >
              {tCart("page.actions.proceedToCheckout")}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CartPage;
